'use strict';

/**
 * dayPortionOverlap — when two portions of the SAME date actually collide (LEAVE-CANON-001 P3d;
 * promoted from palm-api's `timeOffDayPortions` for Phase 3, D-P6-1).
 *
 * Before partial days, sharing a date WAS the collision: every leave request was a whole day. The
 * moment a day can be halved that stops being true, so the date overlap is only the cheap filter and
 * this is the second half of the question, asked of the rows that survive it. Two callers ask it:
 *
 *   - palm, of two leave requests (a morning and an afternoon off are two requests, not a
 *     duplicate);
 *   - planner, of a leave request and a PUBLISHED SHIFT — a shift is simply a `partial` window.
 *
 * One rule, so the employee's duplicate check and the manager's collision flag can never disagree
 * about what "the same time" means.
 *
 * @module status-taxonomy/dayPortionOverlap
 */

const { dayPortionCoversWholeDay, dayPortionRequiresWindow } = require('./constants');

/**
 * `HH:mm` or `HH:mm:ss` → minutes past midnight, for comparing two wall-clock times.
 *
 * @param {string} clock
 * @returns {number}
 */
function clockMinutes(clock) {
  const [hours, minutes, seconds] = String(clock).split(':');
  return Number(hours) * 60 + Number(minutes) + (seconds ? Number(seconds) / 60 : 0);
}

/**
 * Can these two portions of the same day both stand, or do they collide?
 *
 * ⚠️ The mixed case — an explicit window against a `first_half`/`second_half` — is reported as a
 * COLLISION, and that is a deliberate false positive. A half is defined relative to the employee's
 * own working day, and a request does not record where that boundary falls in clock time, so there
 * is no honest way to prove a 10:00–11:00 window sits outside "the first half". The two wrong
 * answers are not symmetric: calling a real collision "clear" double-books someone, while calling a
 * clear day "a collision" costs one extra step (a second request, or a manager's "keep"). The cheap
 * error is the one to make.
 *
 * @param {{dayPortion: string, startTime?: string|null, endTime?: string|null}} a
 * @param {{dayPortion: string, startTime?: string|null, endTime?: string|null}} b
 * @returns {boolean}
 */
function dayPortionsOverlap(a, b) {
  // A whole day contains every other portion there is.
  if (dayPortionCoversWholeDay(a.dayPortion) || dayPortionCoversWholeDay(b.dayPortion)) return true;

  const aTimed = dayPortionRequiresWindow(a.dayPortion);
  const bTimed = dayPortionRequiresWindow(b.dayPortion);

  // Two explicit windows are the one case with enough information to be exact. Touching at an
  // endpoint is not overlapping: 09:00–12:00 and 12:00–15:00 are adjacent, not a double-booking.
  if (aTimed && bTimed) {
    return (
      clockMinutes(a.startTime) < clockMinutes(b.endTime) &&
      clockMinutes(b.startTime) < clockMinutes(a.endTime)
    );
  }

  // One window against one half — unprovable, so treated as a collision (see above).
  if (aTimed || bTimed) return true;

  // Two halves: they collide only if they are the SAME half.
  return a.dayPortion === b.dayPortion;
}

module.exports = { dayPortionsOverlap, clockMinutes };
